/**
 * Declarative fetch component — triggers an API call when `submit` becomes true.
 *
 * Used in Sideways view to perform actions (check-in, uncheck, trace, downlink)
 * by rendering a `<Fetch>` element with the appropriate API URL. The component
 * handles loading state, error handling, and cleanup on unmount.
 *
 * @module Fetch
 */

import React from 'react';

import { isDebug } from './api';
import { fetchStyle } from './styles/constants';
import type { FetchProps } from './types';
import logger from './utils/logger';

/**
 * Fetch component — triggers an HTTP request to `props.api` when `props.submit` is true.
 *
 * Supports GET (default) and POST. For POST, params should be passed via `body` prop
 * rather than as URL query parameters. The Authorization header is included automatically
 * from localStorage when a token is present.
 *
 * @param props.api - URL to fetch (no ?token= needed — use Authorization header)
 * @param props.submit - Trigger fetch when true
 * @param props.method - HTTP method, default 'GET'
 * @param props.body - Request body for POST requests
 * @param props.onResult - Callback with parsed JSON on success
 * @param props.onError - Callback with error on failure
 */
export const Fetch: React.FC<FetchProps> = (props) => {
  const [result, setResult] = React.useState<unknown>(null);
  const [error, setError] = React.useState<Error | { error: string } | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [count, setCount] = React.useState<number>(0);
  const { api, submit, method = 'GET', body } = props;

  React.useEffect(() => {
    let cancelled = false;
    if (!submit)
      return () => {
        cancelled = true;
      };
    if (result || error)
      return () => {
        cancelled = true;
      };
    const f = async (): Promise<void> => {
      try {
        if (cancelled) return;
        if (loading) return;
        logger.debug('Fetching: ', props);
        const token = window.localStorage.getItem('token');
        const fetchOptions: RequestInit = {
          method,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
          },
          ...(method === 'POST' && body ? { body: JSON.stringify(body) } : {}),
        };
        const fetched = await fetch(api, fetchOptions);
        const json = await fetched.json();
        if (!cancelled) {
          logger.debug(json);
          if (json.error) {
            setError(json.error);
            setLoading(() => false);
            props.onError(json.error);
          } else {
            setResult(json);
            setLoading(() => false);
            props.onResult(json);
          }
        }
      } catch (e) {
        if (!cancelled) {
          logger.debug((e as Error).message);
          setError(e as Error);
          setLoading(() => false);
          props.onError(e as Error);
        }
      }
    };
    setLoading(true);
    setCount(() => count + 1);
    setError(null);
    setResult(null);
    f();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit]);
  if (result) return <div />;
  if (!submit) return <div />;
  if (!isDebug()) return loading ? <div style={fetchStyle}>Loading...</div> : <div />;
  if (loading)
    return (
      <div style={fetchStyle}>
        {count}: Fetch {api} - Loading...
      </div>
    );
  return (
    <div style={fetchStyle}>
      {count}: Fetch {api} - Error: {error ? 'present' : 'null'} Result:{' '}
      {result ? 'present' : 'null'}
    </div>
  );
};
